// EnvasadoTalonarios.js
import React, { useState } from 'react';
import Navbar from '../../Navbar';
import { Container, Box, Alert, Snackbar } from '@mui/material';
import TipoTalonarioSelector from './components/TipoTalonarioSelector';
import TalonariosTable from './components/TalonariosTable';
import AltaTalonario from './alta/AltaTalonario';
import EditarTalonario from './editar/EditarTalonario';
import ConfirmDialog from './components/ConfirmDialog';
import { UrlApi } from '../../../services/apirest';

function EnvasadoTalonarios() {
  const [talonarios, setTalonarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTipo, setSelectedTipo] = useState(null);
  
  // Estados para modales
  const [altaOpen, setAltaOpen] = useState(false);
  const [editarOpen, setEditarOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedTalonario, setSelectedTalonario] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  
  // Estados para notificaciones
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Cargar talonarios por tipo
  const cargarTalonarios = async (tipoId) => {
    if (!tipoId) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${UrlApi}/envasado_talonarios/tipo/${tipoId}`);
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      setTalonarios(data);
    } catch (error) {
      console.error('Error al cargar talonarios:', error);
      showSnackbar('Error al cargar los talonarios', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Manejar cambio de tipo
  const handleTipoChange = (tipoId) => {
    setSelectedTipo(tipoId);
    cargarTalonarios(tipoId);
  };

  // Mostrar notificación
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Cerrar notificación
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  // Abrir modal de alta
  const handleNuevoClick = () => {
    if (!selectedTipo) {
      showSnackbar('Primero selecciona un tipo de talonario', 'warning');
      return;
    }
    setAltaOpen(true);
  };

  // Cerrar modal de alta
  const handleCloseAlta = async (success) => {
    setAltaOpen(false);
    if (success) {
      showSnackbar('Talonario creado exitosamente', 'success');
      if (selectedTipo) {
        await cargarTalonarios(selectedTipo);
      }
    }
  };

  // Abrir modal de edición
  const handleEditarClick = (talonario) => {
    setSelectedTalonario(talonario);
    setEditarOpen(true);
  };

  // Cerrar modal de edición
  const handleCloseEditar = async (success) => {
    setEditarOpen(false);
    setSelectedTalonario(null);
    if (success) {
      showSnackbar('Talonario actualizado exitosamente', 'success');
      if (selectedTipo) {
        await cargarTalonarios(selectedTipo);
      }
    }
  };

  // Manejar eliminación
  const handleEliminarClick = (talonario) => {
    setSelectedTalonario(talonario);
    setConfirmAction('delete');
    setConfirmOpen(true);
  };

  // Manejar activación/desactivación
  const handleActivarClick = (talonario) => {
    setSelectedTalonario(talonario);
    setConfirmAction(talonario.activo === 1 ? 'deactivate' : 'activate');
    setConfirmOpen(true);
  };

  // Confirmar acción
  const handleConfirmAction = async () => {
    setConfirmOpen(false);
    
    if (!selectedTalonario) return;

    try {
      if (confirmAction === 'delete') {
        const response = await fetch(`${UrlApi}/envasado_talonarios/${selectedTalonario.id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          }
        });
        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }
        showSnackbar('Talonario eliminado exitosamente', 'success');
      } else if (confirmAction === 'deactivate' || confirmAction === 'activate') {
        const nuevoEstado = confirmAction === 'activate' ? 1 : 0;
        const response = await fetch(`${UrlApi}/envasado_talonarios/${selectedTalonario.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            activo: nuevoEstado
          })
        });
        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }
        showSnackbar(
          `Talonario ${confirmAction === 'activate' ? 'activado' : 'desactivado'} exitosamente`,
          'success'
        );
      }
      
      // Recargar datos
      if (selectedTipo) {
        await cargarTalonarios(selectedTipo);
      }
    } catch (error) {
      console.error('Error en la operación:', error);
      showSnackbar('Error al realizar la operación', 'error');
    }
  };

  // Obtener mensaje del diálogo de confirmación
  const getConfirmDialogConfig = () => {
    if (!selectedTalonario) return null;
    
    const configs = {
      delete: {
        title: 'Eliminar Talonario',
        message: `¿Estás seguro que deseas eliminar el talonario #${selectedTalonario.id} (${selectedTalonario.numero_completo_desde} - ${selectedTalonario.numero_completo_hasta})? Esta acción no se puede deshacer.`,
        confirmColor: 'error',
        confirmText: 'Eliminar'
      },
      deactivate: {
        title: 'Desactivar Talonario',
        message: `¿Estás seguro que deseas desactivar el talonario #${selectedTalonario.id} (${selectedTalonario.numero_completo_desde} - ${selectedTalonario.numero_completo_hasta})?`,
        confirmColor: 'warning',
        confirmText: 'Desactivar'
      },
      activate: {
        title: 'Activar Talonario',
        message: `¿Estás seguro que deseas activar el talonario #${selectedTalonario.id} (${selectedTalonario.numero_completo_desde} - ${selectedTalonario.numero_completo_hasta})?`,
        confirmColor: 'success',
        confirmText: 'Activar'
      }
    };
    
    return configs[confirmAction] || null;
  };

  const confirmConfig = getConfirmDialogConfig();

  return (
    <>
      <Navbar />
      <Container maxWidth="xl" disableGutters sx={{ mb: 2, mt: 1 }}>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <TipoTalonarioSelector 
            onTipoChange={handleTipoChange}
            selectedTipo={selectedTipo}
          />
          {!selectedTipo && (
            <Alert severity="info" sx={{ ml: 2, flex: 1 }}>
              Selecciona un tipo de talonario para comenzar
            </Alert>
          )}
        </Box>
        
        <TalonariosTable
          data={talonarios}
          loading={loading}
          onNuevoClick={handleNuevoClick}
          onEditarClick={handleEditarClick}
          onEliminarClick={handleEliminarClick}
          onActivarClick={handleActivarClick}
        />
      </Container>

      {/* Modal de Alta */}
      <AltaTalonario
        open={altaOpen}
        onClose={handleCloseAlta}
        tipoId={selectedTipo}
      />

      {/* Modal de Edición */}
      {editarOpen && selectedTalonario && (
        <EditarTalonario
          open={editarOpen}
          onClose={handleCloseEditar}
          talonario={selectedTalonario}
        />
      )}

      {/* Diálogo de Confirmación */}
      {confirmConfig && (
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={handleConfirmAction}
          title={confirmConfig.title}
          message={confirmConfig.message}
          confirmColor={confirmConfig.confirmColor}
          confirmText={confirmConfig.confirmText}
        />
      )}

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default EnvasadoTalonarios;